"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { apiClient } from "@/lib/api/client"
import { apiUrl } from "@/lib/api/url"
import { leadKeys } from "@/lib/apps/autoleads/keys/lead"
import { outreachKeys } from "@/lib/apps/autoleads/keys/outreach"
import { syncLeadKeys } from "@/lib/apps/autoleads/keys/sync-leads"
import { taskBoardKeys } from "@/lib/apps/autoleads/keys/task-board"

import type { Lead } from "@/nango/types/apps.autoleads/autoleads/AutoLeads"

// The five fields of a contact the user owns. Everything else on the entry is
// plugin output the backend leaves untouched.
export type LeadContactPayload = {
  name: string
  role: string
  emails: string[]
  phones: string[]
  linkedin_url: string
}

type Options = {
  onSuccess?: (lead: Lead) => void
  successMessage?: string
}

function defaultErrorHandler(err: unknown) {
  const message = err instanceof Error ? err.message : "Une erreur est survenue"
  toast.error(message)
}

// A contact edit moves the roster, the flat contact counts and the outreach
// ladder, so every surface reading a lead is refetched, not only the card.
// The task board included: correcting an email renames the contact's ledger
// key, so its cards, its trail and the hot panel all describe the wrong person
// until they are re-read.
function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: leadKeys.all })
  qc.invalidateQueries({ queryKey: outreachKeys.all })
  qc.invalidateQueries({ queryKey: syncLeadKeys.all })
  qc.invalidateQueries({ queryKey: taskBoardKeys.all })
}

function useContactMutation<TVariables extends { lead_id: number }>(
  path: string,
  options: Options,
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ lead_id, ...data }: TVariables) =>
      apiClient<Lead>({
        url: apiUrl(`api/al/lead/${lead_id}/${path}/`),
        method: "POST",
        data,
      }),
    onSuccess: (lead) => {
      invalidate(qc)
      if (options.successMessage) toast.success(options.successMessage)
      options.onSuccess?.(lead)
    },
    onError: defaultErrorHandler,
  })
}

export function useAddLeadContact(options: Options = {}) {
  return useContactMutation<{ lead_id: number } & LeadContactPayload>(
    "add_contact",
    options,
  )
}

export function useUpdateLeadContact(options: Options = {}) {
  return useContactMutation<
    { lead_id: number; index: number } & LeadContactPayload
  >("update_contact", options)
}

export function useDeleteLeadContact(options: Options = {}) {
  return useContactMutation<{ lead_id: number; index: number }>(
    "delete_contact",
    options,
  )
}
